import { DotLegalDeleteDialog } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { Trans } from "react-i18next";
import { useTranslation } from "../../localization/useTranslation.ts";
import { AccessControlEntity, useAccessResponsibilityHelper } from "./useAccessResponsibilityHelper.hooks.ts";
import GroupCompaniesField from "./GroupCompaniesField.tsx";

export interface GroupCompaniesAccessFieldProps<T extends AccessControlEntity> {
    entity: T | undefined;
    isLoading: boolean;
    onChange: (changeFunc: (entity: T) => void) => Promise<void>;
    onConfirmNavigate: () => void;
    readOnly?: boolean;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
    addGroupEntityDialogHeader: string;
    disableAccessWarning?: boolean;
    confirmAccessRemovalKey: string;
}

function GroupCompaniesAccessField<T extends AccessControlEntity>(props: GroupCompaniesAccessFieldProps<T>) {
    const { translateString } = useTranslation();
    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useAccessResponsibilityHelper<T>(
        props.onChange,
        props.onConfirmNavigate,
        props.disableAccessWarning
    );

    return (
        <>
            <GroupCompaniesField
                groupEntities={props.entity?.groupEntities ?? []}
                isLoading={props.isLoading}
                addGroupEntityDialogHeader={props.addGroupEntityDialogHeader}
                onChange={({ groupEntities, allGroupEntityAccess }) => {
                    const newEntity = { ...props.entity!, groupEntities, allGroupEntityAccess };
                    onFieldChange(newEntity, props.entity!);
                }}
                readOnly={props.readOnly}
                disabled={props.disabled}
                isPreview={props.isPreview}
                errorText={props.errorText}
                allGroupEntityAccess={props.entity?.allGroupEntityAccess ?? true}
                requireSelectionBeforeUncheck={!props.disableAccessWarning}
            />

            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey={props.confirmAccessRemovalKey}
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}
        </>
    );
}

export default GroupCompaniesAccessField;
